// Página de listagem de serviços
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { getServicos, createServico, updateServico, deleteServico } from '../../../services/servicosService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import type { Servico } from '../../../types';

export function Servicos() {
  const { empresaSelecionada, contaId } = useEmpresa();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [servicoSelecionado, setServicoSelecionado] = useState<Servico | null>(null);
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (empresaSelecionada) {
      carregarServicos();
    }
  }, [empresaSelecionada]);

  const carregarServicos = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getServicos(empresaSelecionada.id);
      setServicos(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar serviços');
      console.error('Erro ao carregar serviços:', err);
    } finally {
      setLoading(false);
    }
  };

  const servicosFiltrados = servicos.filter(s =>
    s.descricao.toLowerCase().includes(busca.toLowerCase()) ||
    s.codigo.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (servico?: Servico) => {
    setServicoSelecionado(servico || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setServicoSelecionado(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir serviços');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir este serviço?')) {
      return;
    }

    try {
      await deleteServico(id);
      await carregarServicos();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir serviço');
      console.error('Erro ao excluir serviço:', err);
    }
  };

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Serviços' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar os serviços</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Serviços' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Serviços' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Serviços</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Serviço
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      <Card>
        <Input
          label="Buscar Serviço"
          placeholder="Código ou descrição"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>
      
      <Card>
        <Table
          data={servicosFiltrados}
          columns={[
            { key: 'codigo', label: 'Código' },
            { key: 'descricao', label: 'Descrição' },
            { key: 'unidadeMedida', label: 'Unidade' },
            { key: 'valor', label: 'Valor', render: (item) => formatCurrency(item.valor) },
            { key: 'createdAt', label: 'Cadastrado em', render: (item) => formatDate(item.createdAt) },
            {
              key: 'actions',
              label: 'Ações',
              render: (item) => (
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button onClick={() => abrirModal(item)} className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      
      <Modal isOpen={isModalOpen} onClose={fecharModal} title={servicoSelecionado ? 'Editar Serviço' : 'Novo Serviço'} size="lg">
        <ServicoForm 
          servico={servicoSelecionado} 
          onClose={fecharModal}
          onSuccess={carregarServicos}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      </Modal>
    </div>
  );
}

function ServicoForm({ 
  servico, 
  onClose, 
  onSuccess,
  isSaving,
  setIsSaving
}: { 
  servico: Servico | null; 
  onClose: () => void;
  onSuccess: () => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
}) {
  const { empresaSelecionada, contaId } = useEmpresa();
  const [formData, setFormData] = useState({
    codigo: servico?.codigo || '',
    descricao: servico?.descricao || '',
    unidadeMedida: servico?.unidadeMedida || 'H',
    valor: servico?.valor || 0,
    codigoMunicipal: servico?.codigoMunicipal || '',
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaSelecionada || !contaId) {
      setError('Empresa não selecionada ou sem conta vinculada');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const servicoData = {
        codigo: formData.codigo,
        descricao: formData.descricao,
        unidadeMedida: formData.unidadeMedida,
        valor: formData.valor,
        codigoMunicipal: formData.codigoMunicipal || undefined,
      };

      if (servico) {
        await updateServico(servico.id, servicoData);
      } else {
        await createServico(servicoData, contaId, empresaSelecionada.id);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar serviço');
      console.error('Erro ao salvar serviço:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Código"
          value={formData.codigo}
          onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
          required
          disabled={isSaving}
        />
        <div className="md:col-span-2">
          <Input
            label="Descrição"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            required
            disabled={isSaving}
          />
        </div>
        <Input
          label="Unidade de Medida"
          value={formData.unidadeMedida}
          onChange={(e) => setFormData({ ...formData, unidadeMedida: e.target.value })}
          required
          disabled={isSaving}
          placeholder="H (horas), UN (unidade), etc."
        />
        <Input
          label="Valor"
          type="number"
          step="0.01"
          value={formData.valor}
          onChange={(e) => setFormData({ ...formData, valor: parseFloat(e.target.value) || 0 })}
          required
          disabled={isSaving}
        />
        <Input
          label="Código Municipal"
          value={formData.codigoMunicipal}
          onChange={(e) => setFormData({ ...formData, codigoMunicipal: e.target.value })}
          disabled={isSaving}
        />
      </div>
      
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
